import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export interface RewardDto {
  id: string;
  name: Record<string, string> | string;
  description: Record<string, string> | string | null;
  imageUrl: string | null;
  month: number | null;
  year: number | null;
  phase: number | null;
  periodId: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  minPosition: number;
  maxPosition: number;
  isActive: boolean;
}

export interface CreateRewardInput {
  name: Record<string, string>;
  description?: Record<string, string>;
  imageUrl?: string;
  month?: number;
  year?: number;
  /** T-121: lega il premio all'identità del Periodo di raccolta. */
  periodId?: string;
  minPosition: number;
  maxPosition: number;
  isActive?: boolean;
}

export interface FulfillmentDto {
  contactedAt: string | null;
  unreachableAt: string | null;
  waiverSignedAt: string | null;
  deliveredAt: string | null;
  notes: string | null;
}

export interface RewardWinnerUser {
  id: string;
  displayName: string | null;
  nickname: string | null;
  email: string | null;
}

export interface RewardWinnerDto {
  voucherId: string;
  code: string;
  status: string;
  user: RewardWinnerUser;
  fulfillment: FulfillmentDto | null;
}

export interface RewardReserveDto {
  position: number;
  promotedAt: string | null;
  user: RewardWinnerUser;
}

export interface UpdateFulfillmentInput {
  contacted?: boolean;
  unreachable?: boolean;
  waiverSigned?: boolean;
  delivered?: boolean;
  notes?: string;
}

const QK = {
  list: ['rewards'] as const,
  winners: (id: string) => ['rewards', id, 'winners'] as const,
};

export function useRewardWinners(rewardId: string | null) {
  return useQuery({
    queryKey: QK.winners(rewardId ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<{ winners: RewardWinnerDto[]; reserves: RewardReserveDto[] }>(
        `/rewards/${rewardId}/winners`,
      );
      return res.data;
    },
    enabled: rewardId != null,
  });
}

export function useUpdateFulfillment(rewardId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ voucherId, input }: { voucherId: string; input: UpdateFulfillmentInput }) => {
      const res = await apiClient.patch<{ fulfillment: FulfillmentDto; promotedUserId: string | null }>(
        `/vouchers/${voucherId}/fulfillment`,
        input,
      );
      return res.data;
    },
    onSuccess: () => {
      if (rewardId) qc.invalidateQueries({ queryKey: QK.winners(rewardId) });
    },
  });
}

export function useRewards() {
  return useQuery({
    queryKey: QK.list,
    queryFn: async () => {
      const res = await apiClient.get<{ data: RewardDto[] }>('/rewards');
      return res.data.data;
    },
  });
}

export function useCreateReward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRewardInput) => {
      const res = await apiClient.post<RewardDto>('/rewards', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.list }),
  });
}

export function useDeleteReward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => apiClient.delete(`/rewards/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.list }),
  });
}

export function useEmitVouchers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expiresAt }: { id: string; expiresAt?: string }) => {
      const res = await apiClient.post(
        `/rewards/${id}/emit-vouchers${expiresAt ? `?expiresAt=${expiresAt}` : ''}`,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.list }),
  });
}
